import {
  Box,
  Dialog,
  DialogTitle,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  useTheme,
  Button,
  DialogContent,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { getData } from "../../../../services/library";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

function ResumenPagos(props) {
  const [open, setOpen] = useState(props.open);
  const [datosRow, setDatosRow] = useState(props.datos || {});
  const [resumen, setResumen] = useState([]);
  const theme = useTheme();

  const obtenerResumen = () => {
    getData("/envasado_rendiciones/resumenpagoschofer/" + datosRow.uuid, setResumen);
  };

  useEffect(() => {
    obtenerResumen();
  }, [datosRow]);

  // Procesar datos para agrupar por medio de pago
  const procesarDatos = (data) => {
    const grouped = {};
    
    data.forEach(item => {
      const medioPago = item.medio_pago;
      
      if (!grouped[medioPago]) {
        grouped[medioPago] = {
          medio_pago: medioPago,
          nombre_medio: item.nombre_medio,
          total_general: 0,
          total_envase: 0,
          total_otros: 0,
          cantidad_pedidos_envase: 0,
          cantidad_pedidos_otros: 0
        };
      }
      
      if (item.tipo_pago === 'Envase') {
        grouped[medioPago].total_envase += parseFloat(item.total);
        grouped[medioPago].cantidad_pedidos_envase += item.cantidad_pedidos;
      } else {
        grouped[medioPago].total_otros += parseFloat(item.total);
        grouped[medioPago].cantidad_pedidos_otros += item.cantidad_pedidos;
      }
      
      grouped[medioPago].total_general = grouped[medioPago].total_envase + grouped[medioPago].total_otros;
    });
    
    return Object.values(grouped);
  };

  const datosProcesados = procesarDatos(resumen);

  const handleClose = () => {
    setOpen(false);
    if (props.onClose) {
      props.onClose();
    }
  };

  // Función para obtener color discreto según el medio de pago
  const getPaymentColor = (medioPago) => {
    const colorMap = {
      1: "success", // Efectivo
      6: "primary", // QR
      3: "info", // Débito
      4: "secondary", // Crédito
      2: "warning", // Transferencia
      7: "error", // Otros
    };
    return colorMap[medioPago] || "default";
  };

  // Función para obtener ícono según medio de pago
  const getPaymentIcon = (medioPago) => {
    const iconMap = {
      1: <AttachMoneyIcon fontSize="small" />, // Efectivo
      6: <LocalAtmIcon fontSize="small" />, // QR
      3: <AccountBalanceWalletIcon fontSize="small" />, // Débito
      4: <AccountBalanceWalletIcon fontSize="small" />, // Crédito
      2: <AttachMoneyIcon fontSize="small" />, // Transferencia
    };
    return iconMap[medioPago] || <AttachMoneyIcon fontSize="small" />;
  };

  // Calcular totales generales
  const calcularTotales = () => {
    let totalGeneral = 0;
    let totalEnvase = 0;
    let totalOtros = 0;
    let totalPedidos = 0;
    
    datosProcesados.forEach(item => {
      totalGeneral += item.total_general;
      totalEnvase += item.total_envase;
      totalOtros += item.total_otros;
      totalPedidos += item.cantidad_pedidos_envase + item.cantidad_pedidos_otros;
    });
    
    return { totalGeneral, totalEnvase, totalOtros, totalPedidos };
  };

  const { totalGeneral, totalEnvase, totalOtros, totalPedidos } = calcularTotales();

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Header */}
      <Box justifyContent="center" backgroundColor="#1F618D">
        <DialogTitle>
          <Typography fontSize={20} align="center" color="#ffffff">
            Resumen de pagos de la rendición
          </Typography>
        </DialogTitle>
      </Box>
      
      <DialogContent>
        {datosProcesados.length > 0 ? (
          <Box sx={{ p:0.5 }}>
            {/* Resumen general */}
            <Card variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Resumen General
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total General
                      </Typography>
                      <Typography variant="h5" fontWeight="700" color="primary.main">
                        {formatCurrency(totalGeneral)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Envases
                      </Typography>
                      <Typography variant="h5" fontWeight="700" color="success.main">
                        {formatCurrency(totalEnvase)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Cargas
                      </Typography>
                      <Typography variant="h5" fontWeight="700" color="info.main">
                        {formatCurrency(totalOtros)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Pedidos procesados: <strong>{totalPedidos}</strong>
                </Typography>
              </CardContent>
            </Card>

            <Divider sx={{ my: 2 }}>
              <Chip label="Detalle por Medio de Pago" size="small" />
            </Divider>

            {/* Detalle por medio de pago */}
            <Stack spacing={2}>
              {datosProcesados.map((item) => (
                <Accordion key={item.medio_pago} variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Chip
                        icon={getPaymentIcon(item.medio_pago)}
                        label={item.nombre_medio}
                        color={getPaymentColor(item.medio_pago)}
                        variant="outlined"
                        sx={{ mr: 2 }}
                      />
                      <Typography variant="body1" fontWeight="600">
                        {formatCurrency(item.total_general)}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        {item.cantidad_pedidos_envase + item.cantidad_pedidos_otros} pedidos
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Card variant="outlined" sx={{ bgcolor: 'success.50' }}>
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight="600" color="success.main" gutterBottom>
                              Envases
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">
                                {item.cantidad_pedidos_envase} pedido(s)
                              </Typography>
                              <Typography variant="h6" fontWeight="700" color="success.main">
                                {formatCurrency(item.total_envase)}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Card variant="outlined" sx={{ bgcolor: 'info.50' }}>
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight="600" color="info.main" gutterBottom>
                              Cargas
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">
                                {item.cantidad_pedidos_otros} pedido(s)
                              </Typography>
                              <Typography variant="h6" fontWeight="700" color="info.main">
                                {formatCurrency(item.total_otros)}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>

          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography variant="subtitle1" fontWeight="600" color="text.secondary">
              No hay pagos registrados para esta rendición
            </Typography>
          </Box>
        )}

        {/* Botón Volver */}
        <Grid container justifyContent="flex-end" mt={3}>
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onClick={handleClose}
            >
              Volver
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export default ResumenPagos;